import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Constants } from '../../constants';
import { DomainField, DomainRecord } from '../../models/domain';

export interface DomainApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    count?: number;
    code?: string;
    message?: string;
    error?: { message?: string; error?: string };
}

@Injectable({
    providedIn: 'root'
})
export class DomainService {
    constructor(private http: HttpClient) {}

    private getRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? ''
        });
    }

    domainErrorMessage(response: DomainApiResponse | null | undefined, fallback = 'Errore durante l\'operazione') {
        return response?.error?.message
            || response?.error?.error
            || response?.message
            || response?.code
            || fallback;
    }

    async getDomains(params: Record<string, string | number> = {}): Promise<DomainApiResponse<DomainRecord[]>> {
        try {
            const res = await this.http.get<DomainApiResponse<DomainRecord[]>>(
                environment.apiUrl + '/admin/domains',
                { headers: this.getRequestHeaders(), params: params as any }
            ).toPromise();
            return res ?? { success: false };
        } catch (e) {
            console.error('DomainService getDomains:', e);
            return { success: false };
        }
    }

    async getCompanyDomains(companyId: string): Promise<DomainApiResponse<DomainRecord[]>> {
        try {
            const res = await this.http.get<DomainApiResponse<DomainRecord[]>>(
                environment.apiUrl + `/admin/companies/${companyId}/domains`,
                { headers: this.getRequestHeaders() }
            ).toPromise();
            return res ?? { success: false };
        } catch (e) {
            console.error('DomainService getCompanyDomains:', e);
            return { success: false };
        }
    }

    async addDomain(companyId: string, body: {
        name: string;
        field?: DomainField;
        content?: string;
        includeShop?: boolean;
        shopName?: string;
    }): Promise<DomainApiResponse<DomainRecord[]>> {
        try {
            const res = await this.http.post<DomainApiResponse<DomainRecord | DomainRecord[]>>(
                environment.apiUrl + `/admin/companies/${companyId}/domains`,
                body,
                { headers: this.getRequestHeaders() }
            ).toPromise();
            if (!res) return { success: false };
            const data = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
            return { ...res, data };
        } catch (e) {
            console.error('DomainService addDomain:', e);
            return { success: false };
        }
    }

    async removeDomain(params: {
        companyId?: string;
        recordId?: string;
        field?: DomainField;
    }): Promise<DomainApiResponse<{ success: boolean }>> {
        try {
            if (params.recordId) {
                const query = new URLSearchParams();
                if (params.companyId) query.set('companyId', params.companyId);
                if (params.field) query.set('field', params.field);
                const suffix = query.toString() ? `?${query.toString()}` : '';
                const res = await this.http.delete<DomainApiResponse<{ success: boolean }>>(
                    environment.apiUrl + `/admin/domains/${params.recordId}${suffix}`,
                    { headers: this.getRequestHeaders() }
                ).toPromise();
                return res ?? { success: false };
            }

            if (!params.companyId) {
                return { success: false, message: 'COMPANY_ID_REQUIRED' };
            }

            const query = new URLSearchParams();
            if (params.field) query.set('field', params.field);
            const suffix = query.toString() ? `?${query.toString()}` : '';
            const res = await this.http.delete<DomainApiResponse<{ success: boolean }>>(
                environment.apiUrl + `/admin/companies/${params.companyId}/domains${suffix}`,
                { headers: this.getRequestHeaders() }
            ).toPromise();
            return res ?? { success: false };
        } catch (e) {
            console.error('DomainService removeDomain:', e);
            return { success: false };
        }
    }
}
